"use client";

import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import Link from "next/link";
import { navigation as defaultNavigation, navigationLetsTalk as defaultLetsTalk } from "./navigation-config";
import * as Dialog from "@radix-ui/react-dialog";
import { fontWeights } from "@/utils/fonts";
import { useState } from "react";
import { useRouter } from "next/navigation";

interface NavLink {
  name: string;
  href: string;
  show?: boolean;
}

interface MobileMenuProps {
  navStatus: Record<string, boolean>;
  navLinks?: NavLink[];
  letsTalkLinks?: NavLink[];
  navTextColor?: string;
  navHoverColor?: string;
}

export function MobileMenu({ navStatus, navLinks, letsTalkLinks, navTextColor, navHoverColor }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();

  const handleRouteClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsOpen(false);
    // Allow the menu closing animation to finish before Next.js transitions the page
    setTimeout(() => {
      router.push(href);
    }, 300);
  };

  // Use CMS nav links if provided, otherwise filter from static config
  const visibleNavigation = navLinks ?? defaultNavigation.filter(item => {
    if (item.href === '/') return true;
    const slug = item.href.replace(/^\//, '');
    return navStatus[slug] !== false;
  });

  const letsTalk = letsTalkLinks ?? [...defaultLetsTalk];

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" className="md:hidden bg-white hover:bg-white text-[#002B5B] hover:text-[#002B5B] scale-75 tsm-350:scale-90 px-3 ">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[180px] tsm-350:w-[200px] tsm-400:w-[220px] sm:w-[250px] bg-[#002B5B] text-white" showCloseButton={false}>
        <div className="flex justify-between items-center px-4 py-[3rem]">
          <Dialog.Title style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium, fontSize: "16px" }} className="text-lg font-semibold">
            Menu
          </Dialog.Title>
          <SheetClose asChild className="mt-[-90px] sm:mt-[-85px] md:mt-0   mr-[-10px] sm:mr-[-2px] md:mr-0 ">
            <Button variant="outline" size="icon" className="text-black rounded-full h-7 w-7">
              <X className="h-4 w-4" />
            </Button>
          </SheetClose>
        </div>
        <nav style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium, fontSize: "14px" }} className="flex flex-col mt-4">
          {visibleNavigation.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              onClick={(e) => handleRouteClick(e, item.href)}
              className="transition-colors px-4 py-2"
              style={{ color: navTextColor }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = navHoverColor || '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = navTextColor || '#ffffff';
              }}
            >
              {item.name}
            </Link>
          ))}
          {letsTalk.map((item: any) => (
            <Button
              key={item.name}
              onClick={() => setIsOpen(false)}
              style={{
                fontFamily: "Lato",
                fontWeight: fontWeights.lato.medium,
                fontSize: "14px",
                backgroundColor: item.buttonBgColor || '#FF9202',
                color: item.buttonTextColor || '#ffffff'
              }}
              className="mt-4 p-0 overflow-hidden transition-all duration-300"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = item.buttonHoverBgColor || '#e68302';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = item.buttonBgColor || '#FF9202';
              }}
            >
              <a href={item.href} target="_blank" className="w-full h-full flex items-center justify-center px-4 py-2">
                {item.name}
              </a>
            </Button>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
